import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';

import { type ProductKeys3P } from '../../../../constants/3p-product-configs';
import { type NounKeys } from '../../../../constants/nouns';
import {
	getCategoryForProduct,
	type ProductCategoryConfig,
} from '../../../../constants/product-categories';
import {
	type PrimaryProductKey,
	ProductKeys,
	ProductKeys as ProductKeysObject,
	useProductConfigs,
} from '../../../../constants/products';
import {
	isForgeConnectorProductKey,
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../../constants/schemas/3p-config';
import { SPOTLIGHT_ONBOARDING_STEP1_TARGET } from '../../../../constants/spotlight';
import { type TriggerComponentProps } from '../../../../types';
import { OAuthSource } from '../../../../utils/events/oauth-events';
import {
	serveRightRailImprovementsWithoutExposure,
	serveSearchPageTimeoutsUX,
} from '../../../../utils/experiments';
import { isProductSmartlink } from '../../../../utils/oauth-container';
import { getProductOrder, getProductsOrderByPrimaryProduct } from '../../../../utils/product';
import { getWorkspaceNameFromProduct } from '../../../../utils/search-result-utils';
import { AccordionItem } from '../../../accordion';
import { CustomSpotlightTarget } from '../../../custom-spotlight-target';
import {
	OAuthWarningWrapper,
	type UseIsUserOAuthed,
	type UseOAuthHandlers,
} from '../../../oauth-warning-wrapper';
import { AccordionFilterTrigger } from '../accordion-filter-trigger';
import FilterOption from '../filter-option';
import { messages } from '../messages';
import ShowMoreAppsPopup from '../show-more-apps-popup';

import {
	getNestedProducts,
	getProductOrderWithDynamicSections,
	Sections,
} from './dynamic-sections';
import {
	applyFanoutToHiddenApps,
	createFanoutOptions,
	type FilterOption as UtilsFilterOption,
} from './utils';

type SidebarStoredState = {
	filters: Record<string, { hidden: boolean }>;
};

type FilterOption = UtilsFilterOption & {
	productCategory?: ProductCategoryConfig;
};

export interface AppFilterProps {
	availableProducts: ProductKeys[];
	availableNouns: NounKeys[];
	selectedProduct?: ProductKeys;
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse;
	onSelectProduct: (
		productKey: ProductKeys | undefined,
		datasource?: ThirdPartyConfigsWithAPIResponse | string,
	) => void;
	productResultCountMap: { [key: string]: number };
	isNounsAvailable: boolean;
	useOAuthHandlers: UseOAuthHandlers;
	useIsUserOAuthed: UseIsUserOAuthed;
	sidebarStoredState?: SidebarStoredState;
	onFilterHide: (productKey: ProductKeys) => void;
	onFilterShow: (productKey: ProductKeys) => void;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	searchQuery: string;
	primaryProduct?: PrimaryProductKey;
	isInitialState: boolean;
	recommended3pApps: string[];
	onVisibleProductsToAuthShown: (options: ProductKeys[], recommended3pApps: string[]) => void;
	personalisedProductSections?: Map<Sections, ProductKeys[]>;
	setPersonalisedProductSections?: (sections: Map<Sections, ProductKeys[]>) => void;
	productsWithTimeout?: ProductKeys[];
	resultCountsLoading?: boolean;
}

const dividerStyles = xcss({
	width: '100%',
	borderBlockEnd: 'none',
	borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
	borderInline: 'none',
	marginTop: 'space.075',
	marginBottom: 'space.100',
});

export const Divider = (): React.JSX.Element => <Box xcss={dividerStyles} />;

export const getResultCountForAll = (
	productResultCountMap: { [key: string]: number },
	searchQuery: string,
	selectedProduct?: ProductKeys,
) => {
	// Only Confluence allows an empty query so we should only return the count for Confluence
	if (selectedProduct === ProductKeysObject.Confluence && searchQuery === '') {
		return undefined;
	}

	return (
		Object.keys(productResultCountMap).reduce(
			(count, key) => (count += productResultCountMap[key]),
			0,
		) || undefined
	);
};

const MAX_VISIBLE_APP_FILTERS = 10;
const NEW_MAX_VISIBLE_APP_FILTERS = 15;

const getMaxVisibleAppFilters = () => {
	return serveRightRailImprovementsWithoutExposure()
		? NEW_MAX_VISIBLE_APP_FILTERS
		: MAX_VISIBLE_APP_FILTERS;
};

/**
 * Checks if any datasource for a product is connected (doesn't need OAuth)
 * This is a utility function that avoids using hooks in loops
 */
const hasAnyAuthedDatasource = (
	productKey: ProductKeys,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): boolean => {
	const productConfig = thirdPartyConfigs[productKey as ProductKeys3P];
	return productConfig?.datasources?.some((ds) => !ds?.userNeedsOAuth) ?? false;
};

export const AppFilters = ({
	onSelectProduct,
	recommended3pApps,
	availableProducts,
	availableNouns,
	selectedProduct,
	selectedDatasource,
	isNounsAvailable,
	useOAuthHandlers,
	useIsUserOAuthed,
	productResultCountMap = {},
	sidebarStoredState,
	onFilterHide,
	onFilterShow,
	thirdPartyConfigs,
	searchQuery,
	primaryProduct,
	isInitialState,
	onVisibleProductsToAuthShown,
	personalisedProductSections,
	setPersonalisedProductSections,
	productsWithTimeout,
	resultCountsLoading,
}: AppFilterProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const products = useProductConfigs();

	const visibleFiltersCount = useMemo(() => {
		return Object.values(sidebarStoredState?.filters ?? {}).filter((filter) => {
			return filter && typeof filter === 'object' && 'hidden' in filter && !filter.hidden;
		}).length;
	}, [sidebarStoredState?.filters]);

	const isProductHiddenByUser = useMemo(
		() =>
			(productKey: ProductKeys): boolean => {
				return !!sidebarStoredState?.filters?.[productKey]?.hidden;
			},
		[sidebarStoredState?.filters],
	);

	const productFilters = useMemo(() => {
		// Calculate shouldShowNouns inside the useMemo hook
		let shouldShowNouns: boolean;
		if (!isNounsAvailable) {
			// Convoluting atlassian-web-crawler with nouns is a tech-debt, ideally it should be decoupled from nouns.
			shouldShowNouns =
				availableNouns.length > 0 || availableProducts.includes(ProductKeys.AtlassianWebCrawler);
		} else {
			shouldShowNouns =
				availableProducts.includes(ProductKeys.AtlassianWebCrawler) || isNounsAvailable;
		}

		const availableProductsWithoutAtlasAndCustomWebsitesIfNounsAvailable = shouldShowNouns
			? availableProducts.filter((key) =>
				thirdPartyConfigs[ProductKeys.AtlassianWebCrawler]?.fanOutConnectorSources
					? key !== ProductKeysObject.Atlas
					: key !== ProductKeysObject.Atlas && key !== ProductKeysObject.AtlassianWebCrawler,
			)
			: availableProducts;

		const createProductOption = (productKey: ProductKeys, section?: Sections): FilterOption => {
			const workspaceName = getWorkspaceNameFromProduct(
				productKey as ProductKeys,
				thirdPartyConfigs,
			);
			const isSelected = productKey === selectedProduct && !selectedDatasource;
			// workspaceName is available for Forge connectors and products with displayWorkspaceNameAsProductName
			// Otherwise fall back to displayName from products map
			const label = workspaceName || products[productKey]?.displayName || productKey;
			const count = productResultCountMap[productKey];
			const isPrimaryProduct = productKey === primaryProduct;

			return {
				label,
				count,
				value: productKey as ProductKeys,
				isSelected,
				isPrimaryProduct,
				productCategory: getCategoryForProduct(productKey as ProductKeys),
				onSelect: () => onSelectProduct(productKey),
				hideFilter: () => {
					onFilterHide(productKey as ProductKeys);
					onSelectProduct(undefined);
				},
				showFilter: () => {
					if (visibleFiltersCount >= getMaxVisibleAppFilters()) {
						return;
					}
					return onFilterShow(productKey as ProductKeys);
				},
				resultCountLimit: productKey === ProductKeysObject.Trello ? 30 : undefined,
				section,
				countLoading: resultCountsLoading,
				timeout: productsWithTimeout?.includes(productKey),
			};
		};

		const productsOrder = getProductsOrderByPrimaryProduct(primaryProduct);
		const availableProductsInDefaultOrder =
			availableProductsWithoutAtlasAndCustomWebsitesIfNounsAvailable
				.filter((productKey) => {
					// Forge connectors are dynamically added and won't be in the static products map
					if (isForgeConnectorProductKey(productKey) && fg('rovo-search-dynamic-forge-config')) {
						return true;
					}
					// For hardcoded products, check if they exist in the products map
					return !!products[productKey];
				})
				.sort((a, b) => getProductOrder(a as ProductKeys, b as ProductKeys, productsOrder));

		let productOptions: FilterOption[];
		if (fg('rovo-search-right-rail-personalisation')) {
			// Ignore counts if:
			//  - we're in the initial state (to show the default order)
			//  - when counts are missing- we want to fall back to the default order and show all apps
			const ignoreCounts = isInitialState || Object.keys(productResultCountMap).length === 0;

			let productOrderWithDynamicSections: Map<Sections, ProductKeys[]> = new Map();

			if (serveSearchPageTimeoutsUX()) {
				if (!personalisedProductSections) {
					productOrderWithDynamicSections = getProductOrderWithDynamicSections({
						primaryProduct,
						availableProducts: availableProductsInDefaultOrder,
						productResultCountMap,
						thirdPartyConfigs,
						isProductHiddenByUser,
						ignoreCounts,
						recommended3pApps,
					});

					// Store the order of the personalised sections for future use
					// but only after a successful search has been made
					if (!isInitialState) {
						setPersonalisedProductSections?.(productOrderWithDynamicSections);
					}
				} else {
					productOrderWithDynamicSections = personalisedProductSections;
				}
			} else {
				productOrderWithDynamicSections = getProductOrderWithDynamicSections({
					primaryProduct,
					availableProducts: availableProductsInDefaultOrder,
					productResultCountMap,
					thirdPartyConfigs,
					isProductHiddenByUser,
					ignoreCounts,
					recommended3pApps,
				});
			}

			// Convert to options
			productOptions = Array.from(productOrderWithDynamicSections.entries()).flatMap(
				([section, products]) =>
					products.map((productKey) => createProductOption(productKey, section)),
			);
		} else {
			productOptions = availableProductsInDefaultOrder.map((productKey) =>
				createProductOption(productKey),
			);
		}

		return [
			// All filter
			{
				count: getResultCountForAll(productResultCountMap, searchQuery, selectedProduct),
				label: formatMessage(messages.allFilterLabel),
				value: undefined,
				isSelected: !selectedProduct,
				productCategory: undefined,
				onSelect: () => onSelectProduct(undefined, undefined),
				hideFilter: () => { },
				showFilter: () => { },
				countLoading: resultCountsLoading,
			},
			...productOptions,
		];
	}, [
		availableProducts,
		availableNouns,
		isNounsAvailable,
		formatMessage,
		onSelectProduct,
		selectedProduct,
		selectedDatasource,
		productResultCountMap,
		onFilterHide,
		onFilterShow,
		thirdPartyConfigs,
		products,
		visibleFiltersCount,
		searchQuery,
		primaryProduct,
		isInitialState,
		isProductHiddenByUser,
		recommended3pApps,
		personalisedProductSections,
		setPersonalisedProductSections,
		productsWithTimeout,
		resultCountsLoading,
	]);

	const allApps = useMemo(() => {
		const { visibleApps, hiddenApps } = productFilters.reduce(
			(acc, option) => {
				const visibility = getAppVisibility(
					option,
					sidebarStoredState,
					acc.visibleApps,
					onFilterHide,
					selectedProduct,
					selectedDatasource,
					thirdPartyConfigs,
				);
				if (visibility === 'visible') {
					acc.visibleApps.push(option);
				} else {
					acc.hiddenApps.push(option);
				}
				return acc;
			},
			{ visibleApps: [] as FilterOption[], hiddenApps: [] as FilterOption[] },
		);

		// Note: Special handling for selected datasource is now done in the fanout rendering logic
		// to preserve the original ordering position of the product

		const nestedProducts = getNestedProducts();
		const appsToDisplaySet = new Set<string>();
		const visibleAppsWithCategories = visibleApps
			.filter((visibleApp) => visibleApp != null)
			.filter((visibleApp) => {
				if (fg('rovo-search-right-rail-personalisation')) {
					// Exclude nested products from the main list since they'll be shown within their category.
					// This also mean the nested products (eg. DC) will not be shown in the dropdown- only their parent.
					return !nestedProducts.has(visibleApp.value as ProductKeys);
				}
				return true;
			})
			.map((visibleApp) => {
				return {
					...visibleApp,
					nestedApps:
						// Populate products within its respective category
						visibleApp.productCategory?.products.flatMap((productWithinCategory: ProductKeys) => {
							const nestedApp = visibleApps.find(
								(app) => app && app.value === productWithinCategory,
							);
							if (!nestedApp) {
								return [];
							}

							// Apply fanout logic to nested apps to get correct selection state
							const hasFanout =
								thirdPartyConfigs[nestedApp.value as ProductKeys3P]?.fanOutConnectorSources;

							if (hasFanout) {
								// For fanout products, use the fanout options to get correct selection state
								const fanoutOptions = createFanoutOptions(
									nestedApp,
									thirdPartyConfigs,
									selectedProduct,
									selectedDatasource,
									onSelectProduct,
								);

								// Otherwise return the first fanout option (which will have the correct base state)
								return fanoutOptions;
							}

							return [nestedApp];
						}) ?? [],
				};
			})
			.filter((visibleApp) => {
				// The 'All' case should always be displayed
				if (!visibleApp.value) {
					return true;
				}

				// Don't display apps that are already in a nested category
				if (appsToDisplaySet.has(visibleApp.value)) {
					return false;
				}

				visibleApp.nestedApps.forEach(
					(nestedApp: FilterOption) => nestedApp?.value && appsToDisplaySet.add(nestedApp.value),
				);

				return true;
			});

		// Apply fanout logic to hidden apps for show more popup
		const hiddenAppsWithFanout = applyFanoutToHiddenApps(
			hiddenApps,
			thirdPartyConfigs,
			selectedProduct,
			selectedDatasource,
			onSelectProduct,
		);

		return {
			visibleApps: visibleAppsWithCategories,
			hiddenApps: hiddenAppsWithFanout,
		};
	}, [
		productFilters,
		sidebarStoredState,
		onFilterHide,
		thirdPartyConfigs,
		selectedProduct,
		selectedDatasource,
		onSelectProduct,
	]);

	onVisibleProductsToAuthShown(
		allApps.visibleApps
			.filter(
				(o) =>
					o !== undefined &&
					o.value !== undefined &&
					o.section === Sections.VISIBLE_PRODUCTS_TO_AUTH,
			)
			.map((o) => o.value)
			.filter((o) => o !== undefined),
		recommended3pApps,
	);

	// Memoize the OAuth connection states to avoid recalculating on every render
	const oAuthConnectionStates = useMemo(
		() => ({
			anyConnectedConfluenceDC: hasAnyAuthedDatasource(ProductKeys.ConfluenceDC, thirdPartyConfigs),
			anyConnectedJiraDC: hasAnyAuthedDatasource(ProductKeys.JiraDC, thirdPartyConfigs),
			anyConnectedJsmDC: hasAnyAuthedDatasource(ProductKeys.JsmDC, thirdPartyConfigs),
		}),
		[thirdPartyConfigs],
	);

	const { anyConnectedConfluenceDC, anyConnectedJiraDC, anyConnectedJsmDC } = oAuthConnectionStates;

	return (
		<UFOSegment name="search-page-app-filters">
			<CustomSpotlightTarget name={SPOTLIGHT_ONBOARDING_STEP1_TARGET}>
				<Box role="list">
					{allApps.visibleApps.map((option, index) => {
					if (option.nestedApps.length > 1 && option.productCategory) {
						const FilterTriggerIcon = option.productCategory.Icon;
						const hasSelection = option.nestedApps.find(
							(nestedApp: FilterOption) => nestedApp?.isSelected,
						)?.isSelected;

						const totalCount = option.nestedApps.reduce(
							(prev: number | undefined, curr: FilterOption) =>
								curr.count === undefined ? prev : (prev ?? 0) + curr.count,
							undefined, // Stays undefined if all counts are undefined
						);
						const accordionCategory = option.productCategory.productCategory;

						let defaultIsExpanded = false;

						if (fg('rovo-search-right-rail-personalisation')) {
							// Expand only sections that match the primary product (for Atlas, expand both Jira and Confluence)
							const isPrimaryProductToExpand = primaryProduct === option.value;
							defaultIsExpanded = hasSelection || isPrimaryProductToExpand;
						} else {
							const expandConfluenceApps =
								accordionCategory === ProductKeys.Confluence && anyConnectedConfluenceDC;
							const expandJiraApps = accordionCategory === ProductKeys.Jira && anyConnectedJiraDC;
							const expandJSMApps =
								accordionCategory === ProductKeys.JiraServiceManagement && anyConnectedJsmDC;
							defaultIsExpanded =
								hasSelection || expandConfluenceApps || expandJiraApps || expandJSMApps;
						}

						return (
							<Box role="listitem" key={accordionCategory}>
								<AccordionItem
									triggerComponent={
										<AccordionFilterTrigger
											icon={<FilterTriggerIcon size="small" />}
											label={option.productCategory.displayName}
											count={totalCount}
											countLoading={resultCountsLoading}
										/>
									}
									groupId={option.value || 'unknown'}
									defaultIsExpanded={defaultIsExpanded}
									hasSelection={hasSelection}
									onClick={(openState: boolean) => {
										// If we're opening the selector
										if (!openState) {
											return;
										}
										// And no nested apps are selected
										if (option.nestedApps.find((nested: FilterOption) => nested.isSelected)) {
											return;
										}
										// Select the first one
										option.nestedApps[0]?.onSelect();
									}}
								>
									{option.nestedApps.map((nestedOption: FilterOption) => {
										const isSmartlink = isProductSmartlink({
											productKey: nestedOption.value,
											thirdPartyConfigs,
										});

										return (
											<OAuthWarningWrapper
												datasourceId={nestedOption.datasourceId}
												key={nestedOption.compositeKey || nestedOption.value}
												product={nestedOption.value}
												trigger={({ isDisabled, oAuthWarning, triggerProps }) => (
													<FilterOption
														oAuthWarning={oAuthWarning}
														triggerProps={triggerProps}
														key={nestedOption.compositeKey || nestedOption.value}
														isDisabled={isDisabled}
														option={nestedOption}
														onClick={nestedOption.onSelect}
														onClickMenuItem={(_, action: string) => {
															if (action === 'hide') {
																nestedOption.hideFilter();
															}
														}}
														ariaRole="listitem"
													/>
												)}
												useOAuthHandlers={useOAuthHandlers}
												useIsUserOAuthed={useIsUserOAuthed}
												isSmartlink={isSmartlink}
												oAuthSource={OAuthSource.searchPageFilter}
												thirdPartyConfigs={thirdPartyConfigs}
											/>
										);
									})}
								</AccordionItem>
							</Box>
						);
					}

					// Check if this product has fanout enabled
					const hasFanout =
						thirdPartyConfigs[option.value as ProductKeys3P]?.fanOutConnectorSources;

					if (hasFanout) {
						// Apply fanout only to products that have it enabled
						let optionsToRender = createFanoutOptions(
							option,
							thirdPartyConfigs,
							selectedProduct,
							selectedDatasource,
							onSelectProduct,
						);

						// Special handling for webcrawler products:
						// When a webcrawler product is selected with fanout enabled and show more is enabled,
						if (
							option.value === ProductKeysObject.AtlassianWebCrawler &&
							option.value === selectedProduct &&
							selectedDatasource &&
							allApps.hiddenApps.length > 0
						) {
							optionsToRender = optionsToRender.filter((renderOption) => renderOption.isSelected);
						}

						const isSmartlink = isProductSmartlink({ productKey: option.value, thirdPartyConfigs });

						return optionsToRender.map((renderOption) => {
							const key = renderOption.compositeKey || renderOption.value;
							return (
								<OAuthWarningWrapper
									datasourceId={renderOption.datasourceId}
									key={key}
									product={option.value as ProductKeys}
								trigger={({ isDisabled, oAuthWarning, triggerProps }: TriggerComponentProps) => (
									<FilterOption
										oAuthWarning={oAuthWarning}
										triggerProps={triggerProps}
										key={key}
										isDisabled={isDisabled}
										option={renderOption}
										onClick={renderOption.onSelect}
										onClickMenuItem={(_, action: string) => {
											if (action === 'hide') {
												renderOption.hideFilter();
											}
										}}
										ariaRole="listitem"
									/>
									)}
									index={index}
									useOAuthHandlers={useOAuthHandlers}
									useIsUserOAuthed={useIsUserOAuthed}
									isSmartlink={isSmartlink}
									oAuthSource={OAuthSource.searchPageFilter}
									thirdPartyConfigs={thirdPartyConfigs}
								/>
							);
						});
					} else {
						// Regular product rendering (no fanout)
						const isSmartlink = isProductSmartlink({ productKey: option.value, thirdPartyConfigs });
						return (
							<OAuthWarningWrapper
								datasourceId={selectedDatasource?.datasourceId}
								key={option.value || 'all'}
								product={option.value as ProductKeys}
							trigger={({ isDisabled, oAuthWarning, triggerProps }: TriggerComponentProps) => (
								<FilterOption
									oAuthWarning={oAuthWarning}
									triggerProps={triggerProps}
									key={option.value}
									isDisabled={isDisabled}
									option={option}
									onClick={option.onSelect}
									onClickMenuItem={(_, action: string) => {
										if (action === 'hide') {
											option.hideFilter();
										}
									}}
									ariaRole="listitem"
								/>
								)}
								index={index}
								useOAuthHandlers={useOAuthHandlers}
								useIsUserOAuthed={useIsUserOAuthed}
								isSmartlink={isSmartlink}
								oAuthSource={OAuthSource.searchPageFilter}
								thirdPartyConfigs={thirdPartyConfigs}
							/>
						);
					}
				})}
					<ShowMoreAppsPopup
						options={allApps.hiddenApps}
						useIsUserOAuthed={useIsUserOAuthed}
						useOAuthHandlers={useOAuthHandlers}
						thirdPartyConfigs={thirdPartyConfigs}
						oAuthSource={OAuthSource.searchPageFilter}
					/>
				</Box>
			</CustomSpotlightTarget>
		</UFOSegment>
	);
};

export const getAppVisibility = (
	option: FilterOption,
	sidebarStoredState: SidebarStoredState | undefined,
	visibleApps: FilterOption[],
	onFilterHide: (productKey: ProductKeys) => void,
	selectedProduct?: ProductKeys,
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse | string,
	thirdPartyConfigs?: ThirdPartyConfigsBootstrap,
): 'visible' | 'hidden' => {
	// Always show "All" option or already selected options
	if (option.label === 'All' || option.isSelected) {
		return 'visible';
	}

	// Check if this product matches the selected product
	if (option.value === selectedProduct) {
		const hasFanout =
			thirdPartyConfigs?.[option.value as ProductKeys3P]?.fanOutConnectorSources;

		if (hasFanout) {
			// For fanout products, keep the base product visible to maintain ordering
			// The fanout rendering logic will handle showing the selected datasource
			return 'visible';
		} else {
			// For regular products, only show if no datasource is selected
			return !selectedDatasource ? 'visible' : 'hidden';
		}
	}

	// Logic for the new dynamic sections
	if (
		option.section === Sections.VISIBLE_PRIMARY_PRODUCTS ||
		option.section === Sections.VISIBLE_PRODUCTS_WITH_RESULTS ||
		option.section === Sections.VISIBLE_PRODUCTS_TO_AUTH
	) {
		return 'visible';
	}

	if (option.section === Sections.NESTED_PRODUCTS) {
		// Make it visible since it will be rendered within its category.
		return 'visible';
	}

	if (option.section === Sections.HIDDEN_PRODUCTS) {
		return 'hidden';
	}

	// Always show primary product in the non-dynamic version of right-rail
	if (option.isPrimaryProduct) {
		return 'visible';
	}

	// Check if the user has manually hidden this option
	if (option.value && sidebarStoredState?.filters?.[option.value]?.hidden) {
		return 'hidden';
	}

	if (option.value && visibleApps.length > getMaxVisibleAppFilters()) {
		if (!serveRightRailImprovementsWithoutExposure()) {
			// We should not call this callback since it's meant for user actions only.
			onFilterHide(option.value);
		}
		return 'hidden';
	} else {
		return 'visible';
	}
};
